/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Image,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {PlusIcon, MinusIcon} from '@amblique/king-living/app/components/icons';
import {hasProductSpecificationsContent} from '@amblique/king-living/app/utils/product-utils';
import ProductSupportAccordionContent from '@amblique/king-living/app/pages/product-detail/partials/product-support-accordion-content';

const InformationAccordion = ({product, isSimpleProduct = true}) => {
  const styles = useMultiStyleConfig('ProductDetail', {});
  const [expandedIndices, setExpandedIndices] = useState([]);
  // KINDW-1833: the BM/PIM dimension diagram has no known intrinsic size, so capture its natural
  // dimensions on load to emit correct width/height attributes without guessing the aspect ratio.
  const [dimensionImageSize, setDimensionImageSize] = useState(null);
  const {formatMessage} = useIntl();

  const sections = useMemo(() => {
    const items = [];

    // PIM fields take priority; fall back to legacy SFCC attributes
    if (product?.c_pimlyFeatureOverview) {
      items.push({
        id: 'feature-overview',
        title: formatMessage({
          defaultMessage: 'Feature overview',
          id: 'product_detail.specifications.feature_overview',
        }),
        html: product.c_pimlyFeatureOverview,
      });
    } else if (product?.longDescription) {
      items.push({
        id: 'product-information',
        title: formatMessage({
          defaultMessage: 'Product Information',
          id: 'product_detail.specifications.product_information',
        }),
        html: product.longDescription,
      });
    }

    if (product?.c_pimlyWarrantyInformation) {
      items.push({
        id: 'warranty',
        title: formatMessage({
          defaultMessage: 'Warranty information',
          id: 'product_detail.specifications.warranty_information',
        }),
        html: product.c_pimlyWarrantyInformation,
      });
    }

    if (product?.c_pimlyProductSupport) {
      items.push({
        id: 'support',
        title: formatMessage({
          defaultMessage: 'Product support & care',
          id: 'product_detail.specifications.product_support',
        }),
        html: product.c_pimlyProductSupport,
      });
    }

    // Legacy: What's Included (kept as fallback when PIM fields aren't populated)
    if (product?.c_whatsIncluded) {
      items.push({
        id: 'whats-included',
        title: formatMessage({
          defaultMessage: "What's Included",
          id: 'product_detail.specifications.whats_included',
        }),
        html: product.c_whatsIncluded,
      });
    }

    // Legacy: Measurements
    const hasMeasurements = product?.dimHeight || product?.dimWidth || product?.dimDepth;

    if (hasMeasurements) {
      const rows = [];

      if (product.dimHeight) rows.push(`<div><strong>Height:</strong> ${product.dimHeight}</div>`);
      if (product.dimWidth) rows.push(`<div><strong>Width:</strong> ${product.dimWidth}</div>`);
      if (product.dimDepth) rows.push(`<div><strong>Depth:</strong> ${product.dimDepth}</div>`);

      items.push({
        id: 'measurements',
        title: formatMessage({
          defaultMessage: 'Measurements',
          id: 'product_detail.specifications.measurements',
        }),
        html: rows.join(''),
      });
    }

    return items;
  }, [product, formatMessage]);

  const dimensionImage = product?.c_pimlyDimensionImage;
  const hasSections = sections.length > 0;

  if (!hasProductSpecificationsContent(product)) return null;

  const allIndices = sections.map((_, i) => i);
  const isAllExpanded = expandedIndices.length === allIndices.length && allIndices.length > 0;

  const handleExpandAll = () => {
    setExpandedIndices(isAllExpanded ? [] : allIndices);
  };

  return (
    <Box id="specifications" {...styles.specificationsContainer}>
      <Flex
        direction={{base: 'column', lg: 'row'}}
        gap={dimensionImage ? {base: 8, lg: '4.375rem'} : {base: 8}}
      >
        {/* Left column (desktop) / Stack (mobile): use display:contents so Image can sit between Header and Accordion on mobile */}
        <Box
          display={{base: dimensionImage ? 'contents' : 'block', lg: 'flex'}}
          flexDirection={{lg: 'column'}}
          {...styles.specificationsContainerLeft}
          {...(dimensionImage && styles.specificationsContainerLeftDimensionimage)}
        >
          {/* 1. Header — mobile: first, desktop: first in left column */}
          <Box
            order={{base: 1, lg: 0}}
            {...styles.specificationsHeader}
            {...(dimensionImage && styles.specificationsHeaderDimensionimage)}
          >
            <Text {...styles.specificationsTitle}>
              {formatMessage({
                defaultMessage: 'Specifications',
                id: 'product_detail.tab.title.specifications',
              })}
            </Text>
            {hasSections && dimensionImage && (
              <Button
                aria-label={isAllExpanded ? 'Collapse All' : 'Expand All'}
                variant="ghost"
                {...styles.specificationsExpandAllButton}
                onClick={handleExpandAll}
              >
                <Text {...styles.specificationsExpandAllText}>
                  {formatMessage({
                    defaultMessage: 'Expand all',
                    id: 'product_detail.specifications.expand_all',
                  })}
                </Text>
                <Box {...styles.specificationsExpandAllIconBox}>
                  {isAllExpanded ? (
                    <MinusIcon height="100%" width="100%" />
                  ) : (
                    <PlusIcon height="100%" width="100%" />
                  )}
                </Box>
              </Button>
            )}
          </Box>

          {/* 3. Accordion — mobile: third (after image), desktop: second in left column */}
          {hasSections && (
            <Box order={{base: 3, lg: 0}}>
              <Accordion
                {...styles.specificationsAccordion}
                index={expandedIndices}
                allowMultiple
                onChange={setExpandedIndices}
              >
                {sections.map((section, index) => (
                  <AccordionItem
                    key={section.id}
                    {...styles.specificationsAccordionItem}
                  >
                    {({isExpanded}) => (
                      <>
                        <h2>
                          <AccordionButton {...styles.specificationsAccordionButton} {...(isExpanded && {paddingBottom: {base: 3, lg: undefined}})}>
                            <Box {...styles.specificationsSectionTitle}>
                              {section.title}
                            </Box>
                            <Box {...styles.specificationsIconBox}>
                              {isExpanded ? (
                                <MinusIcon height="100%" width="100%" />
                              ) : (
                                <PlusIcon height="100%" width="100%" />
                              )}
                            </Box>
                          </AccordionButton>
                        </h2>
                        <AccordionPanel {...styles.specificationsAccordionPanel}>
                          {section.id === 'support' ? (
                            <ProductSupportAccordionContent html={section.html} />
                          ) : (
                            <Box
                              as="div"
                              {...styles.specificationsAccordionContent}
                              dangerouslySetInnerHTML={{__html: section.html}}
                            />
                          )}
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                ))}
              </Accordion>
            </Box>
          )}
        </Box>

        {/* 2. Dimension image — mobile: second (between header and accordion), desktop: right column */}
        {dimensionImage && (
          <Box order={{base: 2, lg: 0}} {...styles.specificationsDimensionImageBox}>
            <Image
              htmlHeight={dimensionImageSize?.height}
              htmlWidth={dimensionImageSize?.width}
              maxWidth="100%"
              objectFit="contain"
              src={dimensionImage}
              alt={formatMessage({
                defaultMessage: 'Product dimensions',
                id: 'product_detail.specifications.dimension_image_alt',
              })}
              onLoad={(event) => {
                const {naturalWidth, naturalHeight} = event.currentTarget;

                if (naturalWidth && naturalHeight) {
                  setDimensionImageSize({height: naturalHeight, width: naturalWidth});
                }
              }}
            />
          </Box>
        )}
      </Flex>
    </Box>
  );
};

InformationAccordion.propTypes = {
  product: PropTypes.object,
  isSimpleProduct: PropTypes.bool,
};

export default InformationAccordion;
